import { App, Button, Form, Input, InputNumber, Modal, Select, Switch, Table, Tag } from 'antd';
import type { TableProps } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { hrDepthApi, type EvaluationCriteria, type RatingType } from '@/api/hr-depth';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

interface FormShape {
  name: string;
  ratingType: RatingType;
  maxScore?: number | null;
  sortOrder: number;
}

export function EvaluationCriteriaPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('HR_MGMT:CREATE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('HR_MGMT:EDIT'));

  const [editing, setEditing] = useState<EvaluationCriteria | null>(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm<FormShape>();

  const { data, isLoading } = useQuery({
    queryKey: ['hr', 'evaluation-criteria', 'all'],
    queryFn: () => hrDepthApi.listCriteria(false),
  });

  const upsert = useMutation({
    mutationFn: (v: FormShape) => {
      const body = {
        name: v.name.trim(),
        ratingType: v.ratingType,
        maxScore: v.ratingType === 'NUMERIC' ? Number(v.maxScore ?? 5) : null,
        sortOrder: Number(v.sortOrder),
      };
      return editing ? hrDepthApi.updateCriteria(editing.id, body) : hrDepthApi.createCriteria(body);
    },
    onSuccess: () => {
      message.success(editing ? 'Criterion updated' : 'Criterion created');
      qc.invalidateQueries({ queryKey: ['hr', 'evaluation-criteria'] });
      setEditing(null);
      setCreating(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e, 'Save failed')),
  });

  const toggle = useMutation({
    mutationFn: ({ id, value }: { id: number; value: boolean }) => hrDepthApi.toggleCriteria(id, value),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'evaluation-criteria'] }),
    onError: (e) => message.error(extractError(e, 'Toggle failed')),
  });

  const open = !!editing || creating;
  const startCreate = () => {
    setCreating(true);
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ ratingType: 'NUMERIC', maxScore: 5, sortOrder: (data?.length ?? 0) + 1 });
  };
  const startEdit = (row: EvaluationCriteria) => {
    setEditing(row);
    setCreating(false);
    form.setFieldsValue({
      name: row.name,
      ratingType: row.ratingType,
      maxScore: row.maxScore,
      sortOrder: row.sortOrder,
    });
  };

  const columns: TableProps<EvaluationCriteria>['columns'] = [
    { title: '#', dataIndex: 'sortOrder', width: 50 },
    { title: 'Name', dataIndex: 'name' },
    {
      title: 'Type',
      dataIndex: 'ratingType',
      width: 130,
      render: (v: RatingType, row) =>
        v === 'NUMERIC' ? (
          <Tag color="blue">Numeric / out of {row.maxScore ?? 5}</Tag>
        ) : (
          <Tag>Label</Tag>
        ),
    },
    {
      title: 'Active',
      dataIndex: 'active',
      width: 90,
      render: (v: boolean, row) => (
        <Switch checked={v} disabled={!canEdit} onChange={(value) => toggle.mutate({ id: row.id, value })} />
      ),
    },
    {
      title: '',
      width: 80,
      render: (_, row) =>
        canEdit && (
          <Button size="small" icon={<EditOutlined />} onClick={() => startEdit(row)}>
            Edit
          </Button>
        ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <PageHeader
        eyebrow="HR / Setup"
        title="Performance evaluation criteria"
        description="Aspects that staff are evaluated on. Numeric criteria score out of a configurable maximum (typically 5); label criteria use qualitative grades."
        actions={
          canCreate && (
            <Button type="primary" icon={<PlusOutlined />} onClick={startCreate}>
              Add criterion
            </Button>
          )
        }
      />

      <Table<EvaluationCriteria>
        rowKey="id"
        dataSource={data ?? []}
        loading={isLoading}
        columns={columns}
        pagination={false}
        size="small"
      />

      <Modal
        open={open}
        title={editing ? 'Edit criterion' : 'New criterion'}
        onCancel={() => {
          setEditing(null);
          setCreating(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={upsert.isPending}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" onFinish={(v) => upsert.mutate(v)}>
          <Form.Item label="Name" name="name" rules={[{ required: true, max: 100 }]}>
            <Input placeholder="e.g. Punctuality, Subject Knowledge" />
          </Form.Item>
          <Form.Item label="Rating type" name="ratingType" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'NUMERIC', label: 'Numeric (e.g. 0–5)' },
                { value: 'LABEL', label: 'Label (Excellent / Good / …)' },
              ]}
            />
          </Form.Item>
          <Form.Item shouldUpdate noStyle>
            {({ getFieldValue }) =>
              getFieldValue('ratingType') === 'NUMERIC' && (
                <Form.Item
                  label="Maximum score"
                  name="maxScore"
                  rules={[{ required: true, type: 'number', min: 1, max: 100 }]}
                >
                  <InputNumber style={{ width: '100%' }} min={1} max={100} />
                </Form.Item>
              )
            }
          </Form.Item>
          <Form.Item label="Sort order" name="sortOrder" rules={[{ required: true, type: 'number', min: 1 }]}>
            <InputNumber style={{ width: '100%' }} min={1} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
